import { CheckCircleIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Container,
  Heading,
  HStack,
  Image,
  Link,
  List,
  ListIcon,
  ListItem,
  Stack,
  Text,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { NextSeo } from 'next-seo';
import NextLink from 'next/link';
import Auction from '../components/Auction';
import Footer from '../components/Footer';
import SameTraits from '../components/SameTraits';
import { YearsDaysSinceStart } from './songadao';

const MotionHeading = motion(Heading);

const Home = () => {
  return (
    <>
      <NextSeo
        description="Jonathan Mann writes a song a day, forever."
        title="Home | Song a Day World"
        twitter={{
          cardType: 'summary_large_image',
          handle: '@songadaymann',
          site: 'Song a Day World',
        }}
        openGraph={{
          images: [
            { url: 'https://songaday.world/assets/mint/same-traits/year_1_same_traits.jpg' },
          ],
        }}
      />
      <Auction latest />
      <Container p="4" pb={24} maxW="container.xl" centerContent>
        <Image
          src="/assets/songADAYLogo.png"
          alt="SongADao Logo"
          h={{ base: 32, md: 16 * 3 }}
          loading="eager"
        />
        <Stack spacing={5} align="center" justify="center">
          <MotionHeading
            fontSize={{ base: '5xl', md: '7xl' }}
            textAlign="center"
            animate={{ y: [28, -8, 0], opacity: 1 }}
            initial={{ opacity: 0 }}
            transition={{ delay: 0.6 }}
            maxWidth="container.lg"
            fontWeight="regular"
          >
            A new song, every day.
          </MotionHeading>
          <Box maxWidth="container.sm">
            <YearsDaysSinceStart />
          </Box>
          <Stack direction={{ base: 'column', md: 'row' }}>
            <Link
              href="https://opensea.io/collection/song-a-day"
              passhref="true"
              isExternal
              _hover={{
                textDecoration: 'none',
              }}
            >
              <Button size="lg">Buy on Opensea</Button>
            </Link>
            <NextLink href="/about" passHref>
              <Button variant="outline" size="lg">
                About the project
              </Button>
            </NextLink>
          </Stack>
        </Stack>
      </Container>

      <Box
        bgImage="/assets/mint/social_justice.png"
        bgColor="teal.50"
        bgRepeat="no-repeat"
        backgroundSize="cover"
        bgPosition="bottom"
        pb={32}
        pt={16}
      >
        <Container px={4} maxWidth="container.md">
          <Stack spacing={3}>
            <Heading>What is this?</Heading>
            <Text>
              Song A Day NFT is a growing collection of songs that live on the Ethereum Blockchain.
              It began over 4 years ago with this tweet:
            </Text>
            <Image
              src="/assets/mint/SAD Tweet 1.png"
              alt="Tweet from JMann in 2017 saying he's intrigued by blockchain and has a project idea. He then asked people with skills to DM him"
              w="xs"
              alignSelf="center"
            />

            <Text>
              I've been making one song every day since January 1st, 2009 (Pre-Ethereum!) This
              project represents my entire life's work.
            </Text>
            <Text>
              Song A Day NFT is also what makes you eligible to become a member of{' '}
              <NextLink href="/songadao" passHref>
                <Link color="teal.500" textDecorationLine="underline">
                  SongADAO
                </Link>
              </NextLink>
              .
            </Text>
          </Stack>
        </Container>
      </Box>
      <Container py={16} maxWidth="container.lg">
        <Stack spacing={4}>
          <Heading>The Composition of A S.A.D NFT</Heading>
          <Text>
            Each Song A Day NFT begins with, you guessed it, a SONG! Every song was hand-crafted by
            me, day after day, over the course of the last 13 years. You're literally buying a
            snapshot of my creative life.
          </Text>
          <NextLink href="/explore" passHref>
            <Button variant="outline" w="40" alignSelf="center">
              Explore Songs
            </Button>
          </NextLink>
          <Text>
            The song is documented by a video - the project has lived on{' '}
            <NextLink href="https://www.youtube.com/user/songadaymann" passHref>
              <Link color="teal.500" textDecorationLine="underline" isExternal>
                YouTube
              </Link>
            </NextLink>{' '}
            for all 13 years.
          </Text>

          <Text>
            You also get a totally unique illustration! Each picture is built from hand-drawn layers
            based on the traits of the song
          </Text>
          <Box>
            <Image
              justifyContent="center"
              src="/assets/mint/composition of a song.png"
              alt="Illustrated JMann with arrows pointing to various parts of the image with labels. Mood points to his facial expression, Beard points to his face, Instrument points to the guitar in his hand, Location points to Statue  of Liberty, Topic points to his body"
              alignSelf="center"
              objectFit="cover"
            />
          </Box>
          <Text>
            And each year of S.A.D NFT is illustrated by someone different. No two images are
            identical, each one is 100% unique. All the images below have the exact same traits!
          </Text>

          <List>
            <ListItem>
              <strong>Location:</strong> New York
            </ListItem>
            <ListItem>
              <strong>Topic:</strong> Video games
            </ListItem>
            <ListItem>
              <strong>Instrument:</strong> Acoustic Guitar
            </ListItem>
            <ListItem>
              <strong>Mood:</strong> Excited
            </ListItem>
            <ListItem>
              <strong>Beard:</strong> Beard
            </ListItem>
          </List>
          <SameTraits />
        </Stack>
      </Container>
      <Container maxWidth="container.lg" py="8">
        <Stack spacing={4}>
          <Text>
            There's also a HIDDEN trait, which is the background color. It represents time, which is
            obviously an integral part of Song A Day.
          </Text>

          <Text>
            The background color changes hue by one degree each day, so that the background color of
            Jan. 1 2009 is the same as Jan 1. on every subsequent year. However, the saturation of
            the color (it's vibrancy), is lowered by one degree year over year. This means that the
            background color of January 1, 2021 is the same as January 1, 2009, just less vibrant.
          </Text>

          <Text>This signifies the slow, inevitable march towards death. YAY!</Text>
          <Image
            src="/assets/mint/3d-graph.png"
            alt="3D graph showing how the hues change throughout the years"
            alignSelf="center"
            boxSize="lg"
            objectFit="contain"
          />
        </Stack>
      </Container>
      <Container maxWidth="container.lg" py="8">
        <Stack spacing={4}>
          <Heading>All Songs Are Created Equal</Heading>
          <Text>
            But they're not necessarily the same! Some songs went viral. Some got licensed. Some are
            YOUR favorite, which means they're objectively the most worth owning. But no matter
            which song you get, they're all priced the same (Ξ0.2) and they all make you eligible
            for SongADAO.
          </Text>
          <Text>
            No crazy pricing tiers, no weird fomo curves. Just hit the button, enjoy a random song,
            and then come into the amazing community that is SongADAO.
          </Text>
          <List spacing={3}>
            <Text>
              <strong>What Do I Get?</strong>
            </Text>
            <ListItem>
              <ListIcon as={CheckCircleIcon} color="green.500" />
              The Song, for one
            </ListItem>
            <ListItem>
              <ListIcon as={CheckCircleIcon} color="green.500" />
              The Video
            </ListItem>
            <ListItem>
              <ListIcon as={CheckCircleIcon} color="green.500" />
              The Illustration
            </ListItem>
            <ListItem>
              <ListIcon as={CheckCircleIcon} color="green.500" />
              Eligibility for SongADAO
            </ListItem>
          </List>
          <Text>
            SongADAO is a legal co-op that owns the copyright to every Song A Day song. That means
            that if you're a member, you CO-OWN the rights to every single song. The royalties from
            these songs, both on chain and off, flow into the DAO. In addition, after The Big S.A.D
            Drop, Song A Day will be auctioned off daily, and all that money goes to the DAO as
            well. Read all about it{' '}
            <NextLink href="/songadao" passHref>
              <Link color="teal.500" textDecorationLine="underline">
                here
              </Link>
            </NextLink>
            .
          </Text>
        </Stack>
      </Container>

      <Footer />
    </>
  );
};

export default Home;
